import { cn } from '@morpho-org/ui-v6/utils'
import { cva } from 'class-variance-authority'
import { Slot as RadixSlot } from 'radix-ui'
import { type ComponentProps, forwardRef } from 'react'

/**
 * Card variants matching v5 Card behavior:
 * - flex column/row container (display: flex, as the v5 Card was a Stack)
 * - background: bg-card
 * - borderRadius: rounded-[20px] (20px)
 * - boxShadow: shadow-md dark:shadow-none (@repo shadow-md value, light mode only)
 * - border: optional with withBorder
 * - padding: "s" (20px) / "m" (32px) / "none"
 */
const cardVariants = cva('bg-card shadow-md dark:shadow-none flex rounded-[20px]', {
  variants: {
    cardPadding: {
      s: 'p-md',
      m: 'p-lg',
      none: 'p-0'
    },
    withBorder: {
      true: 'border-[0.5px] border-border',
      false: ''
    }
  },
  defaultVariants: {
    cardPadding: 's',
    withBorder: false
  }
})

export interface CardProps extends ComponentProps<'div'> {
  /** Card padding: "s" (20px), "m" (32px), or "none" */
  padding?: 's' | 'm' | 'none'
  /** Add a border to the card */
  withBorder?: boolean
  /** Render as child element using Radix Slot */
  asChild?: boolean
}

/**
 * Card — a flex container with card-specific visual styling (bg, radius, shadow,
 * optional border, padding). Compose layout with native Tailwind classes via
 * `className` (e.g. `flex-col gap-md`); the previous v5 Stack layout props were
 * removed when the deprecated Box/Stack shim was retired.
 *
 * @example
 * ```tsx
 * <Card padding="m" withBorder className="flex-col gap-md">
 *   <div>Item 1</div>
 *   <div>Item 2</div>
 * </Card>
 * ```
 */
const Card = forwardRef<HTMLDivElement, CardProps>(
  ({ className, padding = 's', withBorder = false, asChild = false, ...props }, ref) => {
    const Comp = asChild ? RadixSlot.Slot : 'div'
    return (
      <Comp
        ref={ref}
        {...props}
        className={cn(cardVariants({ cardPadding: padding, withBorder }), className)}
      />
    )
  }
)
Card.displayName = 'Card'

export { Card, cardVariants }
